import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { audioManager } from '../game/audio/audioManager';
import { getTutorialLevel } from '../game/generation/tutorial';
import { haptics } from '../game/haptics/haptics';
import type { RenderTheme } from '../game/rendering/theme';
import { formatDuration } from '../state/activeTimer';
import { useAppStore } from '../state/store';
import {
  BackIcon,
  Button,
  FitIcon,
  Hearts,
  HintIcon,
  IconButton,
  PauseIcon,
  ProgressBar,
  RestartIcon,
  Sheet,
  Spinner,
  Stat,
} from '../ui/components';
import { BoardView } from './BoardView';

/**
 * The gameplay screen: HUD, board and end-of-level surfaces.
 *
 * Only permitted transitions are reachable — the board stops accepting input as soon as the
 * level is won, lost or paused, and the win/loss sheets are driven by the store's level
 * phase rather than by a collection of booleans.
 */

export interface LevelScreenProps {
  readonly levelNumber: number;
  readonly theme: RenderTheme;
  readonly reducedMotion: boolean;
  onExit(): void;
  onPlayLevel(levelNumber: number): void;
}

export function LevelScreen({
  levelNumber,
  theme,
  reducedMotion,
  onExit,
  onPlayLevel,
}: LevelScreenProps) {
  const {
    levelPhase,
    levelError,
    level,
    snapshot,
    summary,
    hintArrowId,
    focusArrowId,
    paused,
    elapsedMs,
    resumedFromSave,
    boardEpoch,
    settings,
    startLevel,
    tapArrow,
    settleWin,
    requestHint,
    setFocusArrow,
    restartLevel,
    setPaused,
    leaveLevel,
  } = useAppStore(
    useShallow((state) => ({
      levelPhase: state.levelPhase,
      levelError: state.levelError,
      level: state.level,
      snapshot: state.snapshot,
      summary: state.summary,
      hintArrowId: state.hintArrowId,
      focusArrowId: state.focusArrowId,
      paused: state.paused,
      elapsedMs: state.elapsedMs,
      resumedFromSave: state.resumedFromSave,
      boardEpoch: state.boardEpoch,
      settings: state.settings,
      startLevel: state.startLevel,
      tapArrow: state.tapArrow,
      settleWin: state.settleWin,
      requestHint: state.requestHint,
      setFocusArrow: state.setFocusArrow,
      restartLevel: state.restartLevel,
      setPaused: state.setPaused,
      leaveLevel: state.leaveLevel,
    })),
  );

  const [fitSignal, setFitSignal] = useState(0);
  const [coachDismissed, setCoachDismissed] = useState(false);

  useEffect(() => {
    void startLevel(levelNumber, { resume: true });
  }, [levelNumber, startLevel]);

  // Leaving the screen keeps the interrupted level so it can be resumed later.
  useEffect(() => () => leaveLevel(), [leaveLevel]);

  const handleFirstInteraction = useCallback(() => {
    audioManager.unlock();
    haptics.markUserInteraction();
  }, []);

  // Derived from the snapshot rather than from the session: the session is mutable, so a
  // memo keyed on it would not recompute when arrows leave the board.
  const aliveArrowIds = useMemo(() => {
    if (snapshot === null) return [];
    const removed = new Set(snapshot.removedArrowIds);
    const ids: number[] = [];
    for (let id = 0; id < snapshot.totalArrows; id += 1) {
      if (!removed.has(id)) ids.push(id);
    }
    return ids;
  }, [snapshot]);

  const tutorial = summary?.isTutorial === true ? getTutorialLevel(levelNumber) : undefined;
  const interactive = levelPhase === 'ready' && !paused;

  const handleExit = useCallback(() => {
    onExit();
  }, [onExit]);

  if (levelPhase === 'loading' || levelPhase === 'idle') {
    return (
      <div className="ar-screen" data-variant="centred">
        <Spinner label={`Building level ${levelNumber}…`} />
      </div>
    );
  }

  if (levelPhase === 'error' || level === null || snapshot === null) {
    return (
      <div className="ar-screen" data-variant="centred">
        <div className="ar-stack">
          <h1 className="ar-subtitle">This level could not be created</h1>
          <p className="ar-muted">{levelError ?? 'Unknown generation error.'}</p>
          <Button variant="primary" size="block" onClick={() => void startLevel(levelNumber)}>
            Try again
          </Button>
          <Button size="block" onClick={handleExit}>
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  const removed = snapshot.totalArrows - snapshot.remainingArrows;

  return (
    <div className="ar-screen" data-variant="board">
      <header className="ar-hud">
        <IconButton label="Back to home" onClick={handleExit}>
          <BackIcon />
        </IconButton>

        <div className="ar-hud__meta">
          <span className="ar-hud__level">Level {levelNumber}</span>
          <span className="ar-muted">{summary?.difficultyLabel ?? ''}</span>
        </div>

        <div className="ar-spacer" />

        <IconButton
          label="Hint"
          onClick={() => {
            handleFirstInteraction();
            requestHint();
            audioManager.play('ui-tap');
          }}
          disabled={!interactive}
          active={hintArrowId >= 0}
        >
          <HintIcon />
        </IconButton>
        <IconButton label="Fit board to screen" onClick={() => setFitSignal((n) => n + 1)}>
          <FitIcon />
        </IconButton>
        <IconButton label="Restart level" onClick={restartLevel}>
          <RestartIcon />
        </IconButton>
        <IconButton label="Pause" onClick={() => setPaused(true)} disabled={levelPhase !== 'ready'}>
          <PauseIcon />
        </IconButton>
      </header>

      {/* Hearts sit with the progress bar so the control row stays on one line on a phone. */}
      <div className="ar-row ar-status">
        <Hearts remaining={snapshot.heartsRemaining} />
        <ProgressBar
          value={removed}
          max={snapshot.totalArrows}
          label={`${removed} of ${snapshot.totalArrows} arrows cleared`}
        />
      </div>

      {tutorial !== undefined && !coachDismissed && (
        <p className="ar-coach">
          {tutorial.lesson}{' '}
          <button
            type="button"
            className="ar-button"
            data-variant="ghost"
            style={{ minHeight: '2rem', padding: '0 var(--ar-space-2)' }}
            onClick={() => setCoachDismissed(true)}
          >
            Got it
          </button>
        </p>
      )}

      {resumedFromSave && (
        <p className="ar-banner" role="status">
          Picked up where you left off.
        </p>
      )}

      <BoardView
        key={level.boardHash}
        fitSignal={fitSignal}
        boardEpoch={boardEpoch}
        theme={theme}
        reducedMotion={reducedMotion}
        showBlockerHint={settings.showBlockerHint}
        hintArrowId={hintArrowId}
        focusArrowId={focusArrowId}
        aliveArrowIds={aliveArrowIds}
        interactive={interactive}
        board={level.board}
        onActivate={tapArrow}
        onFocusArrow={setFocusArrow}
        onFirstInteraction={handleFirstInteraction}
        onWinSettled={settleWin}
      />

      {paused && levelPhase === 'ready' && (
        <Sheet title="Paused" labelledBy="paused-title">
          <p className="ar-muted">The timer is stopped.</p>
          <div className="ar-stack" style={{ marginTop: 'var(--ar-space-4)' }}>
            <Button variant="primary" size="block" onClick={() => setPaused(false)}>
              Resume
            </Button>
            <Button size="block" onClick={restartLevel}>
              Restart level
            </Button>
            <Button size="block" variant="ghost" onClick={handleExit}>
              Leave level
            </Button>
          </div>
        </Sheet>
      )}

      {levelPhase === 'won' && (
        <Sheet title="Level cleared" labelledBy="won-title">
          <p className="ar-muted">Every arrow is off the board.</p>
          <div className="ar-stats">
            <Stat value={formatDuration(elapsedMs)} label="Time" />
            <Stat value={`${snapshot.mistakes}`} label="Mistakes" />
            <Stat value={`${snapshot.hintsUsed}`} label="Hints" />
          </div>
          <div className="ar-stack">
            <Button variant="primary" size="block" onClick={() => onPlayLevel(levelNumber + 1)}>
              Next level
            </Button>
            <Button size="block" onClick={restartLevel}>
              Play again
            </Button>
            <Button size="block" variant="ghost" onClick={handleExit}>
              Home
            </Button>
          </div>
        </Sheet>
      )}

      {levelPhase === 'lost' && (
        <Sheet title="Out of hearts" labelledBy="lost-title">
          <p className="ar-muted">
            {snapshot.remainingArrows}{' '}
            {snapshot.remainingArrows === 1 ? 'arrow was' : 'arrows were'} still on the board. The
            same puzzle is waiting.
          </p>
          <div className="ar-stack" style={{ marginTop: 'var(--ar-space-4)' }}>
            <Button variant="primary" size="block" onClick={restartLevel}>
              Try again
            </Button>
            <Button size="block" variant="ghost" onClick={handleExit}>
              Home
            </Button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
